import * as THREE from "three";
import { Engine } from "../../game/engine";
import { AudioBed } from "../../game/audio";

/**
 * Splinter — top-down 2D Asteroids; rotate, thrust, fire to break
 * rocks into smaller fragments. World-wrap arena. Death on contact.
 * Game 19 of platform games + 6-game rebrand pack (MVP / v0).
 *
 * Design dossier: docs/dossier-games/19-splinter/
 * Source IP: Atari Asteroids (1979).
 *
 * MVP scope:
 *   - Initial wave: 8 large asteroids from seed, drifting toward center
 *   - Ship: rotate (←/→), thrust (↑), fire (Space), world-wrap
 *   - Bullets: PROJ_SPEED, PROJ_TTL_MS lifetime
 *   - Hit asteroid → splits into 2 smaller (size 3→2→1→vanish)
 *   - Ship-asteroid overlap = death = run ends
 *   - Score = Σ FRAGMENT_WEIGHT[size] per kill
 *   - 90 s run cap
 *
 * Deferred to v2 polish:
 *   - Hyperspace (random teleport with vanish/return risk)
 *   - Wave-progression scheduler
 *   - InstancedMesh polygon pools
 *   - Post-processing
 *   - scores.extra wave_reached
 */

// ============================================================
// Constants — must match scoring-splinter.ts byte-for-byte
// ============================================================

export const RUN_MS = 90_000;
export const ARENA_HALF = 24.0;
export const SAMPLE_INTERVAL_MS = 33;
export const MAX_INPUT_SAMPLES = 2800;
export const MAX_FIRE_EVENTS = 300;

export const ROT_SPEED = 4.0;
export const THRUST = 16.0;
export const MAX_LATERAL = 18.0;
export const SHIP_RADIUS = 0.7;
export const PROJ_SPEED = 32.0;
export const PROJ_TTL_MS = 1200;
export const FIRE_COOLDOWN_MS = 220;

export const INITIAL_WAVE_LARGE = 8;
export const WAVE_SPEED_MULT = 1.30;
export const WAVE_CLEAR_BONUS = 600;
export const HYPERSPACE_COOLDOWN_MS = 4_000;
export const HYPERSPACE_VANISH_MS = 250;
export const SIZE_RADIUS: Record<number, number> = { 1: 0.7, 2: 1.2, 3: 2.0 };
export const SIZE_VELOCITY_SCALE: Record<number, number> = { 1: 2.0, 2: 1.5, 3: 1.0 };
export const FRAGMENT_WEIGHT: Record<number, number> = { 1: 50, 2: 30, 3: 20 };

// ============================================================
// Initial-wave generator — byte-identical with scoring-splinter
// ============================================================

function makeRng(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export interface InitialAsteroid {
  idx: number;
  x: number; y: number;
  vx: number; vy: number;
  size: 3;
}

export function generateWaveN(seed: number, wave_idx: number): InitialAsteroid[] {
  const rng = makeRng((seed ^ 0x53504c54) + wave_idx * 0x9e3779b9);
  const speedScale = Math.pow(WAVE_SPEED_MULT, wave_idx);
  const out: InitialAsteroid[] = [];
  for (let i = 0; i < INITIAL_WAVE_LARGE; i++) {
    const ang = rng() * Math.PI * 2;
    const r = ARENA_HALF * 0.7 + rng() * (ARENA_HALF * 0.25);
    const x = r * Math.cos(ang);
    const y = r * Math.sin(ang);
    const vAng = ang + Math.PI + (rng() - 0.5) * 1.2;
    const vMag = (1.5 + rng() * 2.0) * speedScale;
    out.push({ idx: i, x, y, vx: vMag * Math.cos(vAng), vy: vMag * Math.sin(vAng), size: 3 });
  }
  return out;
}

export function generateInitialWave(seed: number): InitialAsteroid[] {
  return generateWaveN(seed, 0);
}

// ============================================================
// Types
// ============================================================

export interface InputSample {
  t: number;
  rot: -1 | 0 | 1;
  thrust: 0 | 1;
  fire: 0 | 1;
  /** v5: 1 = Shift was pressed this sample (hyperspace trigger). */
  hyperspace?: 0 | 1;
}

export interface SplinterResult {
  kills: number;
  waves_cleared: number;
  hyperspace_hops: number;
  died: boolean;
  duration_ms: number;
  inputs: InputSample[];
  scoreEstimate: number;
}

export interface SplinterCallbacks {
  onHud: (runMs: number, kills: number, alive: number, scoreEstimate: number) => void;
  onGameOver: (result: SplinterResult) => void;
}

// ============================================================
// Game class
// ============================================================

function wrap(v: number, half: number): number {
  const range = half * 2;
  if (v > half) return v - range;
  if (v < -half) return v + range;
  return v;
}

interface LiveAsteroid {
  idx: number;
  size: 1 | 2 | 3;
  x: number; y: number;
  vx: number; vy: number;
  mesh: THREE.Mesh;
}

interface LiveBullet {
  x: number; y: number;
  vx: number; vy: number;
  spawn_t: number;
  mesh: THREE.Mesh;
}

export class Splinter {
  private engine: Engine;
  private audio = new AudioBed();

  // Ship
  private sx = 0; private sy = 0;
  private svx = 0; private svy = 0;
  private sheading = -Math.PI / 2;
  private shipMesh: THREE.Group;

  // Asteroids + bullets
  private asteroids: LiveAsteroid[] = [];
  private bullets: LiveBullet[] = [];
  private nextAstIdx = 0;

  // Input
  private inRotL = false; private inRotR = false;
  private inThrust = false;
  private fireQueued = false;
  private hyperspaceQueued = false;
  private lastFireT = -Infinity;
  // Mobile touch: joystick (x, y) thresholded to rot ∈ {-1,0,1} +
  // thrust ∈ {0,1}. Tank-controls style: lean stick = turn; push
  // stick forward = thrust. Touch overrides keyboard when set.
  private touchRot = 0;
  private touchThrust = false;
  setTouchInput(rot: -1 | 0 | 1, thrust: boolean) {
    this.touchRot = rot;
    this.touchThrust = thrust;
  }
  queueTouchFire() { this.fireQueued = true; }
  queueTouchHyperspace() { this.hyperspaceQueued = true; }
  // v5: hyperspace state
  private hyperRng!: () => number;
  private lastHyperspaceT = -Infinity;
  private vanishUntilT = -Infinity;
  private hyperspaceHops = 0;

  // Recording
  private inputs: InputSample[] = [];
  private sampleTimer = 0;
  private lastSnapshot: InputSample = { t: 0, rot: 0, thrust: 0, fire: 0 };
  private startedAt = 0;
  private kills = 0;
  private wavesCleared = 0;
  private currentWave = 0;
  private spawnSeed = 0;
  private scoreEstimate = 0;
  private gameOver = false;
  private cb: SplinterCallbacks;

  // Reusable bullet geometry
  private bulletGeo: THREE.SphereGeometry;
  private bulletMat: THREE.MeshBasicMaterial;
  private asteroidMatBySize: Record<number, THREE.MeshStandardMaterial> = {};
  // v6 — parallax starfield (3 layers) BELOW the play plane so the
  // top-down camera reads stars as deep void seen through the
  // asteroid field. Per memory feedback-sibling-games-shallow.
  private starLayers: THREE.Points[] = [];
  // v6 — track total initial asteroid mass so adaptive drone +
  // arena-clear signal can normalize against it. Updated as asteroids
  // split / vanish.
  private initialAsteroidCount = 0;
  // v6 — split-counter for the rare-fragment cosmetic surprise. Every
  // 10th split spawns one rare-emissive child. Cosmetic only —
  // scorer is unaffected.
  private splitsSoFar = 0;

  private onKeyDown = (e: KeyboardEvent) => {
    if (e.code === "ArrowLeft" || e.code === "KeyA") this.inRotL = true;
    if (e.code === "ArrowRight" || e.code === "KeyD") this.inRotR = true;
    if (e.code === "ArrowUp" || e.code === "KeyW") this.inThrust = true;
    if (e.code === "Space") { e.preventDefault(); this.fireQueued = true; }
    if (e.code === "ShiftLeft" || e.code === "ShiftRight") { e.preventDefault(); this.hyperspaceQueued = true; }
  };
  private onKeyUp = (e: KeyboardEvent) => {
    if (e.code === "ArrowLeft" || e.code === "KeyA") this.inRotL = false;
    if (e.code === "ArrowRight" || e.code === "KeyD") this.inRotR = false;
    if (e.code === "ArrowUp" || e.code === "KeyW") this.inThrust = false;
  };

  constructor(canvas: HTMLCanvasElement, cb: SplinterCallbacks, seed: number) {
    this.cb = cb;
    this.engine = new Engine(canvas, { onFrame: (dt) => this.frame(dt) });
    this.engine.scene.background = new THREE.Color(0x02030a);
    this.engine.scene.fog = new THREE.Fog(0x02030a, 25, 80);
    this.engine.scene.add(new THREE.AmbientLight(0x405060, 0.5));
    const overhead = new THREE.DirectionalLight(0xffffff, 0.7);
    overhead.position.set(0, 20, 0);
    this.engine.scene.add(overhead);

    // Top-down camera.
    this.engine.camera.position.set(0, 28, 0);
    this.engine.camera.lookAt(0, 0, 0);
    this.engine.camera.fov = 60;
    this.engine.camera.near = 0.1;
    this.engine.camera.far = 100;
    this.engine.camera.updateProjectionMatrix();

    // Arena border — a faint wire ring.
    const borderGeo = new THREE.EdgesGeometry(new THREE.PlaneGeometry(ARENA_HALF * 2, ARENA_HALF * 2));
    const borderMat = new THREE.LineBasicMaterial({ color: 0x2a3050 });
    const border = new THREE.LineSegments(borderGeo, borderMat);
    border.rotation.x = -Math.PI / 2;
    border.position.y = 0;
    this.engine.scene.add(border);

    // Build ship as triangle + glow.
    this.shipMesh = new THREE.Group();
    const shipGeo = new THREE.BufferGeometry();
    const v = new Float32Array([
      0.8, 0, 0,
      -0.5, 0, 0.45,
      -0.5, 0, -0.45,
    ]);
    shipGeo.setAttribute("position", new THREE.BufferAttribute(v, 3));
    shipGeo.setIndex([0, 1, 2]);
    shipGeo.computeVertexNormals();
    const shipMat = new THREE.MeshStandardMaterial({
      color: 0x66e0ff, emissive: 0x88e8ff, emissiveIntensity: 0.7,
      roughness: 0.4, metalness: 0.4, side: THREE.DoubleSide,
    });
    const shipBody = new THREE.Mesh(shipGeo, shipMat);
    shipBody.position.y = 0.2;
    this.shipMesh.add(shipBody);
    this.engine.scene.add(this.shipMesh);

    // Asteroid materials per size.
    this.asteroidMatBySize[3] = new THREE.MeshStandardMaterial({
      color: 0x808090, emissive: 0xa46838, emissiveIntensity: 0.3,
      roughness: 0.85, metalness: 0.15,
    });
    this.asteroidMatBySize[2] = new THREE.MeshStandardMaterial({
      color: 0x9090a0, emissive: 0xa46838, emissiveIntensity: 0.35,
      roughness: 0.85, metalness: 0.15,
    });
    this.asteroidMatBySize[1] = new THREE.MeshStandardMaterial({
      color: 0xa0a0b0, emissive: 0xb47848, emissiveIntensity: 0.4,
      roughness: 0.85, metalness: 0.15,
    });

    // Bullet geometry.
    this.bulletGeo = new THREE.SphereGeometry(0.15, 8, 6);
    this.bulletMat = new THREE.MeshBasicMaterial({ color: 0xffe080 });

    // v6 — parallax starfield BELOW the play plane (y < 0). Top-down
    // camera at y=28 looks through gaps in the asteroid field; stars
    // read as deep void / far galaxies. Three layers at varying depth.
    // Seed-derived so the same starscape recurs per-run.
    const makeStars = (count: number, y: number, radius: number, opts: { size: number; color: number }) => {
      const positions = new Float32Array(count * 3);
      let s = (seed ^ Math.imul(y | 0, 0x9e3779b1)) | 0;
      const r = () => {
        s = (s + 0x6d2b79f5) | 0;
        let t = s;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
      };
      for (let i = 0; i < count; i++) {
        positions[i * 3] = (r() - 0.5) * radius * 2;
        positions[i * 3 + 1] = y + (r() - 0.5) * 8;
        positions[i * 3 + 2] = (r() - 0.5) * radius * 2;
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      const mat = new THREE.PointsMaterial({
        color: opts.color, size: opts.size, sizeAttenuation: true,
        transparent: true, opacity: 0.85, depthWrite: false, fog: false,
      });
      const pts = new THREE.Points(geo, mat);
      this.engine.scene.add(pts);
      this.starLayers.push(pts);
    };
    makeStars(550, -50, 90, { size: 0.5, color: 0x8a92b8 });    // far cool
    makeStars(250, -25, 60, { size: 0.4, color: 0xb0b8d8 });    // mid
    makeStars(100, -10, 40, { size: 0.35, color: 0xffe0c0 });   // near warm

    // Spawn initial wave.
    this.spawnSeed = seed;
    this.hyperRng = makeRng(seed ^ 0x48595053);  // mirror server
    const wave = generateWaveN(seed, 0);
    for (const a of wave) {
      this.asteroids.push(this.spawnAsteroid(a.x, a.y, a.vx, a.vy, 3));
    }
    this.initialAsteroidCount = this.asteroids.length;

    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    this.mountTouch();
  }

  private touchFireEl: HTMLElement | null = null;
  private touchHyperEl: HTMLElement | null = null;
  private onTouchFire = (e: PointerEvent) => { e.preventDefault(); this.fireQueued = true; };
  private onTouchHyper = (e: PointerEvent) => { e.preventDefault(); this.hyperspaceQueued = true; };
  private mountTouch() {
    this.touchFireEl = document.getElementById("splinter-touch-fire");
    this.touchHyperEl = document.getElementById("splinter-touch-hyper");
    if (this.touchFireEl) this.touchFireEl.addEventListener("pointerdown", this.onTouchFire);
    if (this.touchHyperEl) this.touchHyperEl.addEventListener("pointerdown", this.onTouchHyper);
  }
  private unmountTouch() {
    if (this.touchFireEl) this.touchFireEl.removeEventListener("pointerdown", this.onTouchFire);
    if (this.touchHyperEl) this.touchHyperEl.removeEventListener("pointerdown", this.onTouchHyper);
  }

  private spawnAsteroid(x: number, y: number, vx: number, vy: number, size: 1 | 2 | 3): LiveAsteroid {
    const r = SIZE_RADIUS[size];
    // Procedural rock: octagonal cylinder with jitter scale.
    const geo = new THREE.DodecahedronGeometry(r, 0);
    const mesh = new THREE.Mesh(geo, this.asteroidMatBySize[size]);
    mesh.position.set(x, 0.4, y);
    this.engine.scene.add(mesh);
    const a: LiveAsteroid = { idx: this.nextAstIdx++, size, x, y, vx, vy, mesh };
    return a;
  }

  private fire(t: number) {
    if (t - this.lastFireT < FIRE_COOLDOWN_MS) return;
    this.lastFireT = t;
    const m = new THREE.Mesh(this.bulletGeo, this.bulletMat);
    m.position.set(this.sx, 0.35, this.sy);
    this.engine.scene.add(m);
    this.bullets.push({
      x: this.sx, y: this.sy,
      vx: Math.cos(this.sheading) * PROJ_SPEED,
      vy: Math.sin(this.sheading) * PROJ_SPEED,
      spawn_t: t, mesh: m,
    });
    try { this.audio.chime(40); } catch { /* off */ }
  }

  private recordEdge() {
    const kbRot: -1 | 0 | 1 = this.inRotL ? -1 : this.inRotR ? 1 : 0;
    // Touch overrides keyboard so the recorded stream matches what the
    // player is actually doing — server replay would otherwise see a
    // stationary ship while the player drifts forward via touch.
    const rot: -1 | 0 | 1 = this.touchRot !== 0 ? (this.touchRot as -1 | 0 | 1) : kbRot;
    const thrust: 0 | 1 = (this.inThrust || this.touchThrust) ? 1 : 0;
    const fire: 0 | 1 = this.fireQueued ? 1 : 0;
    const hyperspace: 0 | 1 = this.hyperspaceQueued ? 1 : 0;
    const t = Math.round(performance.now() - this.startedAt);
    const snapshot: InputSample = { t, rot, thrust, fire, hyperspace };
    if (this.inputs.length < MAX_INPUT_SAMPLES) this.inputs.push(snapshot);
    this.lastSnapshot = snapshot;
  }

  private frame(dt: number) {
    if (this.gameOver) return;
    const now = performance.now();
    const runMs = now - this.startedAt;
    if (runMs >= RUN_MS) { this.endRun(runMs, false); return; }

    // Boost intensity: ship speed magnitude scaled to MAX_LATERAL.
    // Thrusting at full speed gets the FOV pulse + motion blur kick
    // (depth-bar post-processing).
    const boostSpeed = Math.hypot(this.svx, this.svy);
    this.engine.setBoost(Math.min(1, boostSpeed / MAX_LATERAL), dt);

    // v6 — adaptive audio drone tracks the arena-cleared fraction so
    // the bed thins as you clear and re-thickens when a new wave
    // spawns. Per memory feedback-sibling-games-shallow §"at least
    // one escalation hook". Capped at 0.7. Per-frame cost is trivial.
    try {
      const cleared = this.initialAsteroidCount > 0
        ? 1 - Math.min(1, this.asteroids.length / this.initialAsteroidCount)
        : 0;
      this.audio.setDroneIntensity(0.15 + cleared * 0.55);
    } catch { /* off */ }

    // Sample input at 30 Hz.
    this.sampleTimer += dt * 1000;
    if (this.sampleTimer >= SAMPLE_INTERVAL_MS) {
      this.sampleTimer = 0;
      this.recordEdge();
    }

    // Ship integration. Touch joystick overrides keyboard when active.
    const kbRot = this.inRotL ? -1 : this.inRotR ? 1 : 0;
    const rot = this.touchRot !== 0 ? this.touchRot : kbRot;
    const thrustOn = this.inThrust || this.touchThrust;
    this.sheading += rot * ROT_SPEED * dt;
    if (thrustOn) {
      this.svx += Math.cos(this.sheading) * THRUST * dt;
      this.svy += Math.sin(this.sheading) * THRUST * dt;
    }
    const sp = Math.hypot(this.svx, this.svy);
    if (sp > MAX_LATERAL) { this.svx = (this.svx / sp) * MAX_LATERAL; this.svy = (this.svy / sp) * MAX_LATERAL; }
    this.sx = wrap(this.sx + this.svx * dt, ARENA_HALF);
    this.sy = wrap(this.sy + this.svy * dt, ARENA_HALF);
    this.shipMesh.position.set(this.sx, 0.2, this.sy);
    this.shipMesh.rotation.y = -this.sheading;

    if (this.fireQueued) { this.fire(runMs); this.fireQueued = false; }

    // v5: hyperspace edge-trigger with cooldown.
    if (this.hyperspaceQueued) {
      this.hyperspaceQueued = false;
      if (runMs - this.lastHyperspaceT >= HYPERSPACE_COOLDOWN_MS) {
        const tx = (this.hyperRng() * 2 - 1) * (ARENA_HALF - 1);
        const ty = (this.hyperRng() * 2 - 1) * (ARENA_HALF - 1);
        this.sx = tx; this.sy = ty;
        this.svx = 0; this.svy = 0;
        this.vanishUntilT = runMs + HYPERSPACE_VANISH_MS;
        this.lastHyperspaceT = runMs;
        this.hyperspaceHops += 1;
        this.shipMesh.visible = false;
        try { this.audio.chime(120); } catch { /* off */ }
      }
    }
    // Restore visibility when vanish window expires.
    if (!this.shipMesh.visible && runMs >= this.vanishUntilT) {
      this.shipMesh.visible = true;
    }

    // Bullets.
    for (let i = this.bullets.length - 1; i >= 0; i--) {
      const b = this.bullets[i];
      b.x = wrap(b.x + b.vx * dt, ARENA_HALF);
      b.y = wrap(b.y + b.vy * dt, ARENA_HALF);
      b.mesh.position.set(b.x, 0.35, b.y);
      if (runMs - b.spawn_t > PROJ_TTL_MS) {
        this.engine.scene.remove(b.mesh);
        this.bullets.splice(i, 1);
      }
    }

    // Asteroids.
    for (const a of this.asteroids) {
      a.x = wrap(a.x + a.vx * dt, ARENA_HALF);
      a.y = wrap(a.y + a.vy * dt, ARENA_HALF);
      a.mesh.position.set(a.x, 0.4, a.y);
      a.mesh.rotation.y += dt * 0.4;
    }

    // Bullet-asteroid collisions.
    for (let i = this.bullets.length - 1; i >= 0; i--) {
      const b = this.bullets[i];
      for (let j = this.asteroids.length - 1; j >= 0; j--) {
        const a = this.asteroids[j];
        if (Math.hypot(b.x - a.x, b.y - a.y) < SIZE_RADIUS[a.size]) {
          // Hit. Remove bullet + asteroid.
          this.engine.scene.remove(b.mesh);
          this.bullets.splice(i, 1);
          this.engine.scene.remove(a.mesh);
          this.asteroids.splice(j, 1);
          this.kills += 1;
          this.scoreEstimate += FRAGMENT_WEIGHT[a.size];
          try { this.audio.chime(a.size * 30); } catch { /* off */ }
          // Split.
          if (a.size > 1) {
            const childSize = (a.size - 1) as 1 | 2;
            const baseSpeed = Math.hypot(a.vx, a.vy) * 1.15;
            const ang = Math.atan2(a.vy, a.vx);
            const perp = ang + Math.PI / 2;
            const child1 = this.spawnAsteroid(a.x, a.y, Math.cos(perp) * baseSpeed, Math.sin(perp) * baseSpeed, childSize);
            const child2 = this.spawnAsteroid(a.x, a.y, -Math.cos(perp) * baseSpeed, -Math.sin(perp) * baseSpeed, childSize);
            this.asteroids.push(child1, child2);
            this.splitsSoFar += 1;
            // v6 — every 10th split, the FIRST child fragment gets a
            // rare emissive override. Cosmetic only — scorer is
            // unaffected (FRAGMENT_WEIGHT for a kill is the same
            // regardless of color). Per memory
            // feedback-sibling-games-shallow §"at least one surprise event".
            if (this.splitsSoFar % 10 === 0) {
              const rareMat = (child1.mesh.material as THREE.MeshStandardMaterial).clone();
              const RARE_HUES = [0.78, 0.55, 0.92, 0.18]; // purple, cyan, pink, gold
              const hue = RARE_HUES[(this.splitsSoFar / 10) % RARE_HUES.length | 0];
              rareMat.emissive = new THREE.Color().setHSL(hue, 0.95, 0.65);
              rareMat.color = rareMat.emissive.clone();
              rareMat.emissiveIntensity = 1.2;
              child1.mesh.material = rareMat;
            }
          }
          break;
        }
      }
    }

    // Ship-asteroid collision — skipped while vanishing.
    if (runMs >= this.vanishUntilT) {
      for (const a of this.asteroids) {
        if (Math.hypot(this.sx - a.x, this.sy - a.y) < SIZE_RADIUS[a.size] + SHIP_RADIUS) {
          this.endRun(runMs, true);
          return;
        }
      }
    }

    // Wave-clear → respawn next wave (v2(b) escalation).
    if (this.asteroids.length === 0) {
      this.wavesCleared += 1;
      this.scoreEstimate += WAVE_CLEAR_BONUS;
      this.currentWave += 1;
      const next = generateWaveN(this.spawnSeed, this.currentWave);
      for (const a of next) {
        this.asteroids.push(this.spawnAsteroid(a.x, a.y, a.vx, a.vy, 3));
      }
      try { this.audio.chime(80); } catch { /* off */ }
    }

    this.cb.onHud(runMs, this.kills, this.asteroids.length, this.scoreEstimate);
  }

  private endRun(runMs: number, died: boolean) {
    this.gameOver = true;
    // v6 — quiet the adaptive drone so it doesn't bleed into game-over.
    try { this.audio.setDroneIntensity(0); } catch { /* off */ }
    const result: SplinterResult = {
      kills: this.kills,
      waves_cleared: this.wavesCleared,
      hyperspace_hops: this.hyperspaceHops,
      died,
      duration_ms: Math.round(runMs),
      inputs: this.inputs,
      scoreEstimate: this.scoreEstimate,
    };
    this.cb.onGameOver(result);
  }

  start() {
    this.startedAt = performance.now();
    this.engine.start();
  }
  armAudio() { try { this.audio.arm(); } catch { /* gesture-gated */ } }
  setVolume(v: number) { this.audio.setVolume(v); }
  setMuted(m: boolean) { this.audio.setMuted(m); }
  dispose() {
    this.gameOver = true;
    try { this.audio.setDroneIntensity(0); } catch { /* off */ }
    // v6 — drop the parallax starfield layers.
    for (const layer of this.starLayers) {
      this.engine.scene.remove(layer);
      layer.geometry.dispose();
      (layer.material as THREE.Material).dispose();
    }
    this.starLayers = [];
    this.engine.stop();
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    this.unmountTouch();
  }
}
